/**
 * This file handles triggering metrics beacons.
	- it must have the standard Adobe Beacon loaded on the page:
		/JScript/metrics/adobe/analytics_cdcgov.js
	- it only runs in the browser
	The output of this is a method that builds functions based on the current config (internet or intranet)
 */

import utils from '../utils';
import axios from 'axios';

/**
 *	Base settings
 *		prop3   document.title;
 *		prop48  document.referrer.split('?')[0];
 *		eVar5   '+1' (incrementing)
 *
 *	Page view
 *		prop28  siteLimit
 *		eVar76  newTerms (history of searched terms)
 *
 *	AB Testing for faceted search
 *		prop64  AB Test Name
 *		eVar63  AB Test value
 *
 *	Click event
 *		prop40  'ci-' + label + ': ' + interaction;
 *
 *	Click AB Test
 *		evar18 AB Test value
 */
const getMetrics = (config) => {

	// fetch the current adobe s object
	// this loads asynchronously now, so this must be a function
	const getAdobe = () => {
		return config.isBrowser && window._satellite && 'object' === typeof window._satellite ? window._satellite : false;
	}

	const getAccount = () => utils.cleanString((getAdobe() && getAdobe().account) || 'devcdc');

	let metrics = {
		/**
		 * We use a custom image beacon for CDC link tracking
		 * @param {*} label
		 * @param {*} interaction
		 * @returns true
		 */
		cdcEvent: (label, interaction, props) => {
			let adobe = getAdobe();
			if (!adobe) {
				return;
			}
			interaction = utils.cleanString(interaction || 'click');
			label = utils.cleanString(label || '');
			var imageURL = String(window.location.href).replace(/[\?\#].*$/, '');
			var mTricsPageName = adobe.pageName ? adobe.pageName : document.title;
			var imageTrigger = 'ci-' + label + ': ' + interaction;
			//console.log( "cdc event props:", JSON.stringify( props ) );

			if ( window.hasOwnProperty( '_satellite' ) ) {
				var eventType = null;
				var dataObject = {};
				var _satellite = window._satellite;
				//console.log( "satellite: ", window._satellite );
				eventType = eventType || 'o';
				dataObject.ch = 'OADC';
				dataObject.pageName = mTricsPageName;
				dataObject.prop2 = imageURL;
				//set props from adobe var
				dataObject.prop3 = adobe.prop3;
				dataObject.prop5 = adobe.prop5;
				dataObject.prop28 = adobe.prop28;
				dataObject.prop48 = adobe.prop48;
				dataObject.prop64 = adobe.prop64;
				//set props explicitly
				dataObject.prop31 = imageURL;
				dataObject.prop40 = imageTrigger;
				dataObject.prop46 = imageURL;
				dataObject.prop54 = window.location.href;
				dataObject.prop73 = window.location.href.split('?')[0];
				//set eVars from adobe var
				dataObject.eVar5 = window.adobeLaunch?.eVar5 || 1;
				dataObject.eVar8 = adobe.eVar8;
				dataObject.eVar63 = adobe.eVar63;
				dataObject.eVar76 = adobe.eVar76;
				//set props from individual search result clicks
				if ( props ) {
					dataObject.eVar5 = window.adobeLaunch?.eVar5 || 1;
					dataObject.eVar18 = props.eVar18;
					dataObject.eVar19 = props.eVar19;
					dataObject.eVar20 = props.eVar20;
				}
				dataObject.events = 'prop2,prop3,prop5,prop8,prop14,prop16,prop17,prop27,prop32,prop33,eVar18,eVar19,eVar20,channel';
				dataObject.label = label;
				dataObject.interactionType = eventType;
				dataObject.interactionValue = imageTrigger; //usually prop40 value
				_satellite.track( 'interaction', dataObject );
			}
			return true;
		},

		setProp: (prop, value) => {
			let adobe = getAdobe();
			if (!adobe) {
				return;
			}
			prop = utils.cleanString(prop);
			adobe[prop] = value;
			let linkVars = String(adobe.linkTrackVars || '').split(/\s*,\s*/);
			if (-1 === linkVars.indexOf(prop)) {
				linkVars.push(prop);
				adobe.linkTrackVars = linkVars.join(',');
			}
		},

		/**
		 * This triggers the Adobe metrics track link event
		 * @param {*} e
		 * @param {*} label
		 * @param {*} props
		 * @returns
		 */
		event: (e, label, props) => {
			let adobe = getAdobe();
			let result = true;
			if (!adobe || !label) {
				return result;
			}
			let savePageName = adobe.pageName;
			let navigate = true;
			let interaction = utils.cleanString(interaction || 'click');
			label = utils.cleanString(label || '');
			//console.log('metrics-capture: ' + label + ' - ' + interaction);
			//console.log( "event props: ", JSON.stringify( props ));

			adobe.pageName = document.title;
			if ('object' === typeof props) {
				let keys = [];
				for (let key in props) {
					key = utils.cleanString(key);
					if (!key) {
						continue;
					}
					adobe[key] = props[key];
					keys.push(key);
				}
				keys.push('channel');
				adobe.linkTrackVars = keys.join(',');
			} else {
				adobe.linkTrackVars = 'prop2,prop31,prop40,prop49,prop46,channel';
				adobe.prop40 = 'ci-' + label + ': ' + interaction;
			}

			// for abtest add another prop
			if (config.abtest) {
				// evar18: ab value
				let siteLimit = utils.cleanTerm(utils.getParam('sitelimit', utils.getParam('siteLimit', '')));
				if (siteLimit.match(/coronavirus/)) {
					let abtestvalue = config.faceted ? 'A' : 'B';
					adobe.eVar18 = abtestvalue;
					adobe.linkTrackVars += ',eVar18';
				}
			}

			let element = e && e.target;
			let elementType = String((element && element.nodeName) || 'div').toLowerCase();
			if ('a' === elementType && navigate) {
				// Determine the type of link and only use navigate option on s.tl for standard links off page.
				var href = element.getAttribute('href');
				var target = element.getAttribute('target');
				if (target && '_blank' === target) {
					navigate = false;
				} else if (href && 0 < href.length && '#' === href[0]) {
					navigate = false;
				}
			}
			metrics.cdcEvent(label, interaction, props);
			adobe.pageName = savePageName;
			return result;
		},

		/**
		 * Triggers an Adobe page track event / beacon
		 * @returns
		 */
		page: () => {
			let adobe = getAdobe();
			if (!adobe) {
				return;
			}
			adobe.pageURL = String(window.location.href);

			if ( window.hasOwnProperty( '_satellite' ) ) {
				var dataObject = {};
				var _satellite = window._satellite;
				dataObject.prop28 = adobe.prop28;
				dataObject.ch = 'OADC';
				_satellite.track( 'pageview', dataObject );
			}

		},

		/**
		 * This updates the Adobe props before a Page view is triggered
		 * @param {*} data
		 * @returns
		 */
		updateProps: (data) => {
			let adobe = getAdobe();
			if (!adobe) {
				return;
			}

			let page = utils.toNumber((data && data.dpage) || utils.getParam('dpage', 1));
			let query = utils.cleanTerm((data && data.query) || utils.getParam('query') || '');
			let siteLimit = utils.cleanTerm(
				(data && data.siteLimit) || utils.getParam('sitelimit', utils.getParam('siteLimit', ''))
			);

			adobe.pageName = 'Search Results - Page ' + page;
			adobe.prop28 = siteLimit;

			let key = 'cdc_search_history';
			let past = String((window.sessionStorage && window.sessionStorage.getItem(key)) || '');
			let terms = past ? past.split(/\s*\|\s*/) : [];
			if (query) {
				if (!terms.length || query !== terms[terms.length - 1]) {
					terms.push(query);
				}
				let newTerms = terms.join('|');
				adobe.eVar63 = query;
				adobe.eVar76 = newTerms;
				window.sessionStorage && window.sessionStorage.setItem(key, newTerms);
			}

			// Adding A/B test reporting logic requirements
			if (config.abtest) {
				// Faceted Search AB Testing
				if (siteLimit.match(/coronavirus/)) {
					let abtest = utils.cleanString(config.abtest);
					let abtestvalue = config.faceted ? 'A' : 'B';

					// report on faceted search options
					let filters = {
						topic: data ? data.topic : utils.fromPiped(utils.getParam('topic', '')),
						contenttype: data ? data.contenttype : utils.fromPiped(utils.getParam('topcontenttypeic', '')),
						audience: data ? data.audience : utils.fromPiped(utils.getParam('audience', '')),
					};
					let filterParts = [];
					for (let filter in filters) {
						if (Array.isArray(filters[filter]) && filters[filter].length) {
							filterParts.push(filter + ':' + filters[filter].join(','));
						}
					}
					if (filterParts.length) {
						adobe.prop40 = filterParts.join('|');
					}

					adobe.prop64 = abtest;
					adobe.eVar8 = abtestvalue;
					config.debug && console.info(abtest + ': ' + abtestvalue);
				}
			}
		},

		init: () => {
			if ( 'object' !== typeof window.adobeLaunch || 'undefined' === typeof window.adobeLaunch ) {
				window.adobeLaunch = {
					eVar5: 1
				};
			}
		}
	};

	metrics.init();

	// trigger update props on load
	metrics.updateProps();

	return metrics;
};

export default getMetrics;
